import React from 'react';
import { useNavigate } from 'react-router-dom';

const FLAG_CONFIG = {
    REST: { emoji: '🔴', label: 'Rest Today', bg: 'bg-red-500/10 border-red-500/20', text: 'text-red-400' },
    MODIFIED: { emoji: '🟡', label: 'Modified', bg: 'bg-yellow-500/10 border-yellow-500/20', text: 'text-yellow-400' },
    PUSH: { emoji: '🟢', label: 'Push Today', bg: 'bg-green-500/10 border-green-500/20', text: 'text-green-400' },
};

const INTENSITY_STYLES = {
    Recovery: 'bg-green-500/20 text-green-400 border-green-500/30',
    Low: 'bg-green-500/20 text-green-400 border-green-500/30',
    Medium: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
    High: 'bg-red-500/20 text-red-400 border-red-500/30',
    Moderate: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
};

function DrillCard({ drill }) {
    return (
        <div className="bg-gray-700/40 rounded-xl p-4 border border-gray-600/40 flex flex-col gap-2">
            <div className="flex items-center justify-between gap-2 flex-wrap">
                <p className="font-bold text-white text-sm">{drill.name}</p>
                <span className="bg-gray-700 text-gray-300 border border-gray-600 px-2 py-0.5 rounded-lg text-xs font-bold">
                    ⏱ {drill.duration_min} min
                </span>
            </div>
            {drill.setup && (
                <p className="text-gray-400 text-xs leading-relaxed">{drill.setup}</p>
            )}
            {drill.coaching_cue && (
                <p className="text-purple-400 text-xs leading-relaxed border-t border-white/5 pt-2">
                    🎯 {drill.coaching_cue}
                </p>
            )}
        </div>
    );
}

function SessionBlock({ block, index }) {
    const blockColors = [
        'border-orange-500/30 bg-orange-500/5',
        'border-blue-500/30 bg-blue-500/5',
        'border-purple-500/30 bg-purple-500/5',
        'border-green-500/30 bg-green-500/5',
    ];
    const color = blockColors[index % blockColors.length];

    return (
        <div className={`rounded-2xl border p-5 ${color}`}>
            <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
                <div>
                    <p className="font-black text-white text-sm uppercase tracking-wider">
                        {block.name}
                    </p>
                    {block.objective && (
                        <p className="text-gray-400 text-xs mt-0.5">{block.objective}</p>
                    )}
                </div>
                <span className="bg-gray-800 text-gray-300 border border-gray-700 px-3 py-1 rounded-lg text-xs font-bold">
                    {block.duration_min} min
                </span>
            </div>
            <div className="flex flex-col gap-3">
                {block.drills?.map((drill, i) => (
                    <DrillCard key={i} drill={drill} />
                ))}
            </div>
        </div>
    );
}

function formatPlanForWhatsApp(plan) {
    let msg = `*${plan.session_title}*\n`;
    msg += `Intensity: ${plan.intensity_level}\n`;
    if (plan.coach_note) msg += `\n📋 ${plan.coach_note}\n`;
    msg += '\n';
    plan.blocks?.forEach(b => {
        msg += `*${b.name} — ${b.duration_min} min*\n`;
        b.drills?.forEach(d => {
            msg += `• ${d.name} (${d.duration_min} min)\n`;
        });
        msg += '\n';
    });
    if (plan.modifications?.length > 0) {
        msg += `*Individual Notes:*\n`;
        plan.modifications.forEach(m => {
            const flag = FLAG_CONFIG[m.flag];
            msg += `${flag?.emoji || '•'} ${m.athlete_name}: ${m.modification}\n`;
        });
    }
    msg += `\n— Generated by AthleteIQ`;
    return msg;
}

export default function SessionPlanOutput({ plan, onReset, squadSummary }) {
    const navigate = useNavigate();

    const handleWhatsApp = () => {
        const text = formatPlanForWhatsApp(plan);
        window.open(`https://wa.me/?text=${encodeURIComponent(text)}`);
    };

    return (
        <div className="flex flex-col gap-6">

            {/* Header */}
            <div className="bg-gray-800 rounded-2xl border border-gray-700 p-5">
                <div className="flex items-start justify-between gap-3 flex-wrap">
                    <div>
                        <h2 className="text-xl font-black text-white">{plan.session_title}</h2>
                        {plan.coach_note && (
                            <p className="text-gray-400 text-sm mt-2 leading-relaxed">
                                💬 {plan.coach_note}
                            </p>
                        )}
                    </div>
                    <span className={`px-3 py-1 rounded-xl text-xs font-black border ${INTENSITY_STYLES[plan.intensity_level] || 'bg-gray-700 text-gray-400 border-gray-600'}`}>
                        {plan.intensity_level}
                    </span>
                </div>

                {/* Warning */}
                {plan.warning && (
                    <div className="mt-4 bg-yellow-500/10 border border-yellow-500/20 rounded-xl px-4 py-3">
                        <p className="text-yellow-400 text-xs font-bold">⚠️ {plan.warning}</p>
                    </div>
                )}

                {/* Squad summary chips */}
                {squadSummary && (
                    <div className="flex gap-2 mt-4 flex-wrap">
                        <span className="bg-gray-700/60 border border-gray-600/50 text-gray-300 px-3 py-1 rounded-lg text-xs font-bold">
                            👥 {squadSummary.total} athletes
                        </span>
                        {squadSummary.restCount > 0 && (
                            <span className="bg-red-500/10 border border-red-500/20 text-red-400 px-3 py-1 rounded-lg text-xs font-bold">
                                🔴 {squadSummary.restCount} rest
                            </span>
                        )}
                        {squadSummary.modifiedCount > 0 && (
                            <span className="bg-yellow-500/10 border border-yellow-500/20 text-yellow-400 px-3 py-1 rounded-lg text-xs font-bold">
                                🟡 {squadSummary.modifiedCount} modified
                            </span>
                        )}
                        {squadSummary.pushCount > 0 && (
                            <span className="bg-green-500/10 border border-green-500/20 text-green-400 px-3 py-1 rounded-lg text-xs font-bold">
                                🟢 {squadSummary.pushCount} push
                            </span>
                        )}
                    </div>
                )}
            </div>

            {/* Session Blocks */}
            <div className="flex flex-col gap-4">
                {plan.blocks?.map((block, i) => (
                    <SessionBlock key={i} block={block} index={i} />
                ))}
            </div>

            {/* Modification Table */}
            {plan.modifications?.length > 0 && (
                <div className="bg-gray-800 rounded-2xl border border-gray-700 p-5">
                    <h3 className="text-sm font-black text-white uppercase tracking-widest mb-4">
                        Individual Modifications
                    </h3>
                    <div className="flex flex-col gap-3">
                        {plan.modifications.map((m, i) => {
                            const flag = FLAG_CONFIG[m.flag] || FLAG_CONFIG['MODIFIED'];
                            return (
                                <div key={i} className={`rounded-xl border p-3 ${flag.bg} flex flex-col gap-1`}>
                                    <div className="flex items-center justify-between flex-wrap gap-2">
                                        <p className="font-bold text-white text-sm">
                                            {flag.emoji} {m.athlete_name}
                                        </p>
                                        <span className={`text-xs font-black ${flag.text}`}>
                                            {flag.label}
                                        </span>
                                    </div>
                                    {m.modification && (
                                        <p className="text-gray-400 text-xs leading-relaxed">
                                            {m.modification}
                                        </p>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}

            {/* Action Buttons */}
            <div className="flex gap-3 flex-wrap">
                <button
                    onClick={onReset}
                    className="border border-gray-600 text-gray-400 px-5 py-2.5 rounded-xl text-sm font-bold hover:border-purple-500 hover:text-purple-400 transition"
                >
                    ← New Session
                </button>
                <button
                    onClick={handleWhatsApp}
                    className="bg-green-600 hover:bg-green-500 text-white px-5 py-2.5 rounded-xl text-sm font-bold transition"
                >
                    📲 Share on WhatsApp
                </button>
                <button
                    onClick={() => navigate('/dashboard')}
                    className="border border-gray-600 text-gray-400 px-5 py-2.5 rounded-xl text-sm font-bold hover:border-blue-500 hover:text-blue-400 transition"
                >
                    ← Dashboard
                </button>
            </div>

        </div>
    );
}